import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { ACCOUNT_KEY, getAccount, type Account } from "../lib/account";
import type { Rating } from "../lib/types";
import { AuthContext, type AuthMode, type AuthValue, type RegisterInput } from "./context";
import { apiLogin, apiLogout, apiMe, apiRegister } from "./api";
import { AuthModal } from "../components/AuthModal";

// One writer for the shared "rearcade.account" key, so the un-bundled
// highscores.js and the game bridges always see the same identity.
function persist(account: Account | null) {
  try {
    if (account) localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
    else localStorage.removeItem(ACCOUNT_KEY);
  } catch {
    /* private mode — in-memory only */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccountState] = useState<Account | null>(() => getAccount());
  const [ratings, setRatings] = useState<Rating[] | null>(null);
  const [mode, setMode] = useState<AuthMode | null>(null);

  const setAccount = useCallback((a: Account | null) => {
    persist(a);
    setRatings(null);
    setAccountState(a);
  }, []);

  // Cross-tab sync (the "open in two tabs" multiplayer flow): storage events fire
  // only in OTHER tabs, so this won't double-handle the originating tab.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACCOUNT_KEY) setAccountState(getAccount());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const refreshMe = useCallback(async (): Promise<Rating[] | null> => {
    const data = await apiMe();
    if (data?.revoked) {
      setAccount(null);
      return null;
    }
    if (data && Array.isArray(data.ratings)) {
      setRatings(data.ratings as Rating[]);
      return data.ratings as Rating[];
    }
    return null;
  }, [setAccount]);

  // Validate the stored session against the server on mount (signs out if revoked).
  useEffect(() => {
    if (getAccount()) void refreshMe();
  }, [refreshMe]);

  const register = useCallback(
    async (input: RegisterInput) => {
      const d = await apiRegister(input);
      setAccount({ token: d.token, username: d.username, email: d.email ?? undefined, expiresAt: d.expiresAt });
    },
    [setAccount],
  );

  const signIn = useCallback(
    async (input: { username: string; password: string }) => {
      const d = await apiLogin(input);
      setAccount({ token: d.token, username: d.username, email: d.email ?? undefined, expiresAt: d.expiresAt });
    },
    [setAccount],
  );

  const signOut = useCallback(async () => {
    await apiLogout();
    setAccount(null);
  }, [setAccount]);

  const value: AuthValue = useMemo(
    () => ({
      account,
      ratings,
      register,
      signIn,
      signOut,
      refreshMe,
      openAuth: (m: AuthMode = "signin") => setMode(m),
      closeAuth: () => setMode(null),
    }),
    [account, ratings, register, signIn, signOut, refreshMe],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {mode && <AuthModal mode={mode} onClose={() => setMode(null)} />}
    </AuthContext.Provider>
  );
}
